import { useEffect, useRef, useState } from 'react';
import globalWindow from '@shared/core/globals';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { addError } from '@shared/utils/logger';
import { useBundledApplicatorEnabled } from '@shared/utils/styleApplicator/ThemeStylesProvider';
import type { StyleApplicatorApi } from '@shared/utils/styleApplicator/styleApplicator.types';
import { withWindow } from '@shared/utils/withWindow';

export type CleanupFunction = () => void;

export type ApplicatorResolveValue = Readonly<{
  hasAppliedSprites: boolean;
  cleanUp: CleanupFunction;
}>;

export type GatherApplicatorCallsCallback = (applicator: StyleApplicatorApi) => Promise<ApplicatorResolveValue>[];

/**
 * LEGACY-ONLY (PRNT-4898): drives the external applicator.js script's window.StyleApplicator.
 * When the websiteBundleStyleApplicator flag is affirmatively 'on', theme art renders as React
 * (ThemeSprites/useThemeCanvas) and this hook is inert. The decision comes from
 * ThemeStylesProvider's page-wide latch, which useThemeCanvas reads too, so the two can never
 * disagree and hand the same node to a second writer (PRNT-4856). With the flag off or
 * unresolved this behaves exactly as it does on main.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useStyleApplicator = (gatherApplicatorCalls: GatherApplicatorCallsCallback, dependencies: unknown[]) => {
  const mounted = useIsMounted();
  const [legacyLoading, setIsLoading] = useState(true);
  const useBundledApplicator = useBundledApplicatorEnabled();
  // Nothing imperative runs on the bundled path, so there is nothing to wait for. Derived rather
  // than pushed through the effect with a setState, which cost an extra render on every mount.
  // The owning layout hook surfaces useThemeCanvas's loading in its place.
  const loading = useBundledApplicator ? false : legacyLoading;
  const handler = useEventCallback((styleApplicator: StyleApplicatorApi) => {
    return gatherApplicatorCalls(styleApplicator);
  });

  const appliedSpritesRef = useRef<CleanupFunction[]>([]);

  useEffect(() => {
    // Theme art is rendered by ThemeSprites on the bundled path — this hook owns nothing there.
    if (useBundledApplicator) {
      return;
    }

    let timeout: ReturnType<typeof setTimeout>;
    withWindow(() => {
      if (!mounted) {
        return;
      }

      function tryApplyingStyles() {
        setIsLoading(true);
        if (!globalWindow.StyleApplicator) {
          return;
        }
        Promise.all(handler(globalWindow.StyleApplicator))
          .then(results => {
            const currentSprites = appliedSpritesRef.current;
            appliedSpritesRef.current = [];
            currentSprites.forEach(cleanUp => cleanUp?.());

            results.forEach(result => {
              if (result.hasAppliedSprites) {
                appliedSpritesRef.current.push(result.cleanUp);
              }
            });
          })
          .catch(error => {
            addError(error, { context: 'useStyleApplicator', operation: 'applyStyles' });
          })
          .finally(() => setIsLoading(false));
      }

      // NOT FUN, need to wait for the style-applicator script to
      // be loaded in the Helmet, i.e. <Head /> of the page
      // see GuestSiteHead.tsx
      const waitForStyles = (time: number) => {
        if (globalWindow.StyleApplicator) {
          return tryApplyingStyles();
        }
        timeout = setTimeout(() => {
          waitForStyles(time * 1.5);
        }, time);
      };
      waitForStyles(200);
    });
    return () => {
      if (timeout) {
        withWindow(() => {
          clearTimeout(timeout);
        });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, handler, useBundledApplicator, ...dependencies]);

  return { loading };
};
